import { Archive, Check, Mail, Paperclip, Star, Trash2 } from "lucide-react";
import { useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import type { MailThread } from "@/lib/api";
import {
  mailSwipeActionLabel,
  type MailSwipeActionId,
} from "@/mail/prefs";

const SWIPE_THRESHOLD = 72;
const SWIPE_MAX = 112;
/** Prefer vertical scroll — only lock horizontal when clearly sideways. */
const HORIZ_BIAS = 1.15;

type Props = {
  thread: MailThread;
  selected: boolean;
  showAccount: boolean;
  accountLabel?: string;
  fromLabel: string;
  chainCount: number;
  /** Red Sent chip. Sent folder rows are mail we sent. */
  showSent?: boolean;
  hasAttach: boolean;
  listWhen: string;
  subject: string;
  snippet: string;
  leftAction: MailSwipeActionId;
  rightAction: MailSwipeActionId;
  onOpen: () => void;
  onSwipeAction: (action: MailSwipeActionId) => void;
};

function actionIcon(action: MailSwipeActionId): ReactNode {
  switch (action) {
    case "archive":
      return <Archive size={18} aria-hidden />;
    case "delete":
      return <Trash2 size={18} aria-hidden />;
    case "read":
      return <Check size={18} aria-hidden />;
    case "unread":
      return <Mail size={18} aria-hidden />;
    case "toggle-read":
      return <Mail size={18} aria-hidden />;
    case "star":
      return <Star size={18} aria-hidden />;
    default:
      return null;
  }
}

function actionClass(action: MailSwipeActionId): string {
  if (action === "delete") return "danger";
  if (action === "archive") return "archive";
  if (action === "star") return "star";
  if (action === "none") return "none";
  return "read";
}

function releasePointer(target: EventTarget | null, pointerId: number | null) {
  if (!(target instanceof HTMLElement) || pointerId == null) return;
  try {
    if (target.hasPointerCapture(pointerId)) target.releasePointerCapture(pointerId);
  } catch {
    /* ignore */
  }
}

export function MailListRow({
  thread,
  selected,
  showAccount,
  accountLabel,
  fromLabel,
  chainCount,
  showSent = false,
  hasAttach,
  listWhen,
  subject,
  snippet,
  leftAction,
  rightAction,
  onOpen,
  onSwipeAction,
}: Props) {
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startX = useRef<number | null>(null);
  const startY = useRef<number | null>(null);
  const axis = useRef<"x" | "y" | null>(null);
  const pointerId = useRef<number | null>(null);
  const frontRef = useRef<HTMLDivElement | null>(null);
  const suppressClick = useRef(false);
  const showChain = chainCount > 1;

  function resetDrag() {
    releasePointer(frontRef.current, pointerId.current);
    startX.current = null;
    startY.current = null;
    axis.current = null;
    pointerId.current = null;
    setDragging(false);
    setOffset(0);
  }

  function onPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    // Track only — do NOT capture yet. Capturing on down blocks native
    // vertical list scroll (stuck after scrolling to the bottom).
    startX.current = event.clientX;
    startY.current = event.clientY;
    axis.current = null;
    pointerId.current = event.pointerId;
  }

  function onPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (startX.current == null || startY.current == null) return;
    const dx = event.clientX - startX.current;
    const dy = event.clientY - startY.current;
    if (!axis.current) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      axis.current = Math.abs(dx) > Math.abs(dy) * HORIZ_BIAS ? "x" : "y";
      if (axis.current === "y") {
        // Vertical scroll — drop tracking so the list pane owns the gesture.
        resetDrag();
        return;
      }
      setDragging(true);
      pointerId.current = event.pointerId;
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
    }
    if (axis.current !== "x") return;
    let next = dx;
    if (next < 0 && leftAction === "none") next = 0;
    if (next > 0 && rightAction === "none") next = 0;
    next = Math.max(-SWIPE_MAX, Math.min(SWIPE_MAX, next));
    setOffset(next);
  }

  function onPointerUp() {
    if (startX.current == null) {
      resetDrag();
      return;
    }
    const committed = offset;
    const locked = axis.current;
    if (locked === "x" && Math.abs(committed) >= SWIPE_THRESHOLD) {
      suppressClick.current = true;
      if (committed < 0 && leftAction !== "none") onSwipeAction(leftAction);
      else if (committed > 0 && rightAction !== "none") onSwipeAction(rightAction);
    }
    resetDrag();
  }

  function onClickRow() {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    onOpen();
  }

  const revealLeft = offset < -8 && leftAction !== "none";
  const revealRight = offset > 8 && rightAction !== "none";

  return (
    <li className="mail-row-swipe">
      <div
        className={`mail-row-swipe-bg left ${actionClass(leftAction)}${revealLeft ? " show" : ""}`}
        aria-hidden
      >
        {actionIcon(leftAction)}
        <span>{mailSwipeActionLabel(leftAction)}</span>
      </div>
      <div
        className={`mail-row-swipe-bg right ${actionClass(rightAction)}${revealRight ? " show" : ""}`}
        aria-hidden
      >
        {actionIcon(rightAction)}
        <span>{mailSwipeActionLabel(rightAction)}</span>
      </div>
      <div
        ref={frontRef}
        className={`mail-row-swipe-front${dragging ? " dragging" : ""}`}
        style={{ transform: `translate3d(${offset}px,0,0)` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={resetDrag}
      >
        <button
          type="button"
          className={`mail-row${selected ? " active" : ""}${thread.unread ? " is-unread" : ""}`}
          aria-current={selected ? "true" : undefined}
          data-selected={selected ? "true" : undefined}
          onClick={onClickRow}
        >
          {showAccount && accountLabel ? (
            <span className="mail-row-account">{accountLabel}</span>
          ) : null}
          <div className="mail-row-top">
            <span className="mail-row-from-wrap">
              {thread.unread ? <i className="mail-row-unread-dot" aria-label="Unread" /> : null}
              <strong className={thread.unread ? "unread" : undefined}>{fromLabel}</strong>
              {showSent ? <span className="mail-sent-label">Sent</span> : null}
              {showChain ? (
                <span
                  className="mail-row-chain"
                  title={`${chainCount} messages in chain`}
                  aria-label={`${chainCount} messages in chain`}
                >
                  {chainCount > 99 ? "99+" : String(chainCount)}
                </span>
              ) : null}
              {hasAttach ? (
                <span className="mail-row-attach" title="Has attachment" aria-label="Has attachment">
                  <Paperclip size={14} aria-hidden />
                </span>
              ) : null}
            </span>
            <div className="mail-row-indicators">
              {thread.starred ? (
                <span className="mail-row-star" title="Starred" aria-label="Starred">
                  <Star size={14} aria-hidden />
                </span>
              ) : null}
              {listWhen ? <span className="mail-row-time">{listWhen}</span> : null}
            </div>
          </div>
          <p className="mail-subject">{subject}</p>
          {snippet ? <p className="mail-snippet">{snippet}</p> : null}
        </button>
      </div>
    </li>
  );
}
